import React, { useState } from 'react';
import './DesktopShortcut.css';

function DesktopShortcut({ shortcut, onOpen, onEdit, onDelete, onMove, moveTargets = [], isDragging, isDropTarget, onDragStart, onDragEnd, unreadCount = 0 }) {
  const [contextMenu, setContextMenu] = useState(null);
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  const handleDoubleClick = () => {
    onOpen(shortcut);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
    setShowMoveMenu(false);
  };

  const handleEdit = () => {
    setContextMenu(null);
    onEdit(shortcut);
  };

  const handleDelete = () => {
    setContextMenu(null);
    if (window.confirm(`Delete shortcut to "${shortcut.title}"?\n\n(The actual ${shortcut.type === 'note' ? 'note' : 'app'} will not be deleted)`)) {
      onDelete(shortcut.id);
    }
  };

  const handleMouseDown = (e) => {
    if (e.button === 0) { // Left click only
      onDragStart(shortcut.id, e);
    }
  };

  // Close context menu when clicking anywhere
  React.useEffect(() => {
    if (contextMenu) {
      const closeMenu = () => setContextMenu(null);
      document.addEventListener('click', closeMenu);
      return () => document.removeEventListener('click', closeMenu);
    }
  }, [contextMenu]);

  return (
    <>
      <div
        className={`desktop-shortcut ${isDragging ? 'dragging' : ''} ${isDropTarget ? 'drop-target' : ''}`}
        style={{
          left: `${shortcut.position.x}px`,
          top: `${shortcut.position.y}px`
        }}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        onMouseDown={handleMouseDown}
      >
        <div className="desktop-shortcut-icon">
          {shortcut.type === 'url' && shortcut.url ? (
            <img 
              src={`https://www.google.com/s2/favicons?domain=${new URL(shortcut.url).hostname}&sz=64`}
              alt={shortcut.title}
              className="url-favicon"
              onError={(e) => {
                // Fallback to default icon if favicon fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : null}
          {shortcut.type === 'url' ? (
            <span className="url-fallback-icon" style={{display: 'none'}}>🌐</span>
          ) : (
            shortcut.icon
          )}
          {unreadCount > 0 && (
            <div className="shortcut-badge">{unreadCount > 99 ? '99+' : unreadCount}</div>
          )}
        </div>
        <div className="desktop-shortcut-title">{shortcut.title}</div>
      </div>

      {contextMenu && (
        <div
          className="desktop-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button onClick={handleDoubleClick}>Open</button>
          <button onClick={handleEdit}>✏️ Edit</button>
          {/* Build 27 (item 5): Move this item to another location.
              v1.0.1 fix: previously required moveTargets.length > 0,
              which hid the whole submenu when the only folder was the
              one the shortcut was already in — leaving no way to move
              it back out to the root. Now: show the option whenever
              there is ANY valid destination. "Desktop (root)" is a
              valid destination iff the shortcut is currently inside a
              folder. Other folders are valid destinations if they're
              not the shortcut's current folder. */}
          {(() => {
            if (!onMove || shortcut.type === 'folder') return null;
            const inAFolder = !!shortcut.folderId;
            const otherFolders = moveTargets.filter(t => t.id !== shortcut.folderId);
            const hasAnyDestination = inAFolder || otherFolders.length > 0;
            if (!hasAnyDestination) return null;
            return (
              <div
                className="context-move-wrapper"
                onMouseEnter={() => setShowMoveMenu(true)}
                onMouseLeave={() => setShowMoveMenu(false)}
              >
                <button className="has-flyout">📂 Move to… ›</button>
                {showMoveMenu && (
                  <div className="desktop-context-submenu">
                    {inAFolder && (
                      <button
                        onClick={() => {
                          setContextMenu(null);
                          setShowMoveMenu(false);
                          onMove(shortcut, null); // null = desktop root
                        }}
                      >
                        🖥️ Desktop (root)
                      </button>
                    )}
                    {otherFolders.map(t => (
                      <button
                        key={t.id}
                        onClick={() => {
                          setContextMenu(null);
                          setShowMoveMenu(false);
                          onMove(shortcut, t.id);
                        }}
                      >
                        📁 {t.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          <button onClick={handleDelete} className="delete-option">Delete Shortcut</button>
        </div>
      )}
    </>
  );
}

export default DesktopShortcut;
